import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Home,
  School,
  Users,
  BookOpen,
  FileText,
  Calendar,
  Shield,
  Settings,
  User,
  UserCheck,
  GraduationCap,
  ClipboardList,
  MessageSquare,
  BarChart3,
  LogOut,
  Brain
} from 'lucide-react';
import './Sidebar.css';

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [schoolData, setSchoolData] = useState(null);
  const [loadingSchoolData, setLoadingSchoolData] = useState(false);

  // Buscar dados da escola quando o usuário é school_adm
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (user?.profile?.school_adm && user?.profile?.school_id) {
        setLoadingSchoolData(true);
        try {
          const { data, error } = await supabase
            .from('escolas')
            .select('id, nome, user_chat')
            .eq('id', user.profile.school_id)
            .single();

          if (error) {
            console.error('Erro ao buscar dados da escola:', error);
          } else {
            setSchoolData(data);
          }
        } catch (error) {
          console.error('Erro ao buscar dados da escola:', error);
        } finally {
          setLoadingSchoolData(false);
        }
      }
    };

    fetchSchoolData();
  }, [user?.profile?.school_adm, user?.profile?.school_id]);

  // Se não é admin, não renderizar o sidebar
  if (!user || user.userType !== 'admin') {
    return null;
  }

  const handleLogout = () => {
    if (window.confirm('Tem certeza que deseja sair?')) {
      logout();
    }
  };

  // Verificar se deve mostrar o menu Conversas
  const shouldShowConversas = () => {
    // Se não é school_adm, sempre mostrar
    if (!user?.profile?.school_adm) {
      return true;
    }
    
    // Se é school_adm mas ainda está carregando dados da escola, não mostrar
    if (loadingSchoolData) {
      return false;
    }
    
    // Se é school_adm, só mostrar se a escola permite user_chat
    return schoolData?.user_chat === true;
  };

  const administrativoItems = [
    { path: '/escolas', icon: School, label: 'Escolas' },
    { path: '/usuarios-administrativos', icon: Users, label: 'Usuários Admin' },
    { path: '/materias', icon: BookOpen, label: 'Matérias' },
    { path: '/conteudos', icon: FileText, label: 'Conteúdos' },
    { path: '/anos-escolares', icon: Calendar, label: 'Anos Escolares' },
    { path: '/contextos-ia', icon: Brain, label: 'Contextos de IA' },
    { path: '/termos-uso', icon: Shield, label: 'Termos de uso' }
  ];

  const escolasItems = [
    { path: '/usuarios', icon: User, label: 'Usuários' },
    { path: '/funcoes-usuarios', icon: UserCheck, label: 'Funções de usuários' },
    { path: '/alunos', icon: GraduationCap, label: 'Alunos' },
    { path: '/testes', icon: ClipboardList, label: 'Testes' },
    { path: '/respostas', icon: FileText, label: 'Respostas' },
    { path: '/relatorios', icon: BarChart3, label: 'Relatórios' },
    ...(shouldShowConversas() ? [{ path: '/conversas', icon: MessageSquare, label: 'Conversas' }] : [])
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <img src="/logo.png" alt="INDEC Evolua" className="logo-image" />
        </div>
      </div>

      <nav className="sidebar-nav">
        {/* Dashboard - Ocultar para school_adm */}
        {!user?.profile?.school_adm && (
          <Link 
            to="/" 
            className={`nav-item ${isActive('/') ? 'active' : ''}`}
          >
            <Home className="nav-icon" />
            <span>Painel de Controle</span>
          </Link>
        )}

        {/* Administrativo Section - Ocultar se school_adm = true */}
        {!user?.profile?.school_adm && (
          <div className="nav-section">
            <div className="section-title-simple">
              <Settings className="section-icon" />
              <span>Administrativo</span>
            </div>
            
            <div className="section-items">
              {administrativoItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <item.icon className="nav-icon" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Escolas Section */}
        <div className="nav-section">
          <div className="section-title-simple">
            <School className="section-icon" />
            <span>Escolas</span>
          </div>
          
          <div className="section-items">
            {escolasItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon className="nav-icon" />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* User Profile and Logout */}
      <div className="sidebar-footer">
        <div className="user-profile">
          <div className="user-avatar">
            <User size={20} />
          </div>
          <div className="user-info">
            <div className="user-name">{user?.profile?.name || 'Usuário'}</div>
            <div className="user-role">{user?.email || 'Email não disponível'}</div>
          </div>
        </div>
        
        <button className="logout-button" onClick={handleLogout} title="Sair">
          Sair
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
